import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useMarketplace } from '../../context/MarketplaceContext';
import { useTranslation } from 'react-i18next';
import { useTCoins } from '../../context/TCoinsContext';
import { createAdminVideoCallBooking } from '../../utils/verificationDatabase';
import { getVideoCallCost } from '../../data/verificationModels';

const AdminVideoCallBookingModal = ({ isOpen, onClose, onSuccess, store, purpose = 'verification' }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { getStoreSubscription } = useMarketplace();
  const { getTotalTCoins, getTotalReal, getTotalVirtual, deductTCoins } = useTCoins();
  const [loading, setLoading] = useState(false);
  
  // Form Data
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedDuration, setSelectedDuration] = useState(30); // 15, 30, 60 minutes
  const [purposeDescription, setPurposeDescription] = useState('');
  const [packageInfo, setPackageInfo] = useState(null);
  const [cost, setCost] = useState(500);
  const [usingPackage, setUsingPackage] = useState(false);
  
  // Available time slots (Admin working hours)
  const availableTimes = ['09:00', '10:00', '11:00', '13:00', '14:00', '15:00', '16:00'];
  
  useEffect(() => {
    if (isOpen && store?.id) {
      // Set default date to tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setSelectedDate(tomorrow.toISOString().split('T')[0]);
      
      loadPackageInfo();
    }
  }, [isOpen, store]);
  
  useEffect(() => {
    if (selectedDuration) {
      // Reset cost first
      const calculatedCost = getVideoCallCost(selectedDuration);
      setCost(calculatedCost);
      setUsingPackage(false);
      
      // Check if package covers this
      if (packageInfo) {
        checkPackageCoverage();
      }
    }
  }, [selectedDuration, packageInfo]);
  
  const loadPackageInfo = async () => {
    try {
      if (store?.id) {
        const subscription = await getStoreSubscription(store.id);
        setPackageInfo(subscription);
        checkPackageCoverage();
      }
    } catch (error) {
      console.error('Error loading package info:', error);
    }
  };
  
  const checkPackageCoverage = () => {
    if (!packageInfo) return;
    
    const packageType = packageInfo.packageType;
    
    // Premium Package benefits
    if (packageType === 'premium') {
      if (selectedDuration === 15) {
        // Free 2 times/month
        setUsingPackage(true);
        setCost(0);
      } else if (selectedDuration === 30) {
        // Free 1 time/month
        setUsingPackage(true);
        setCost(0);
      } else if (selectedDuration === 60) {
        // 50% discount
        setUsingPackage(true);
        setCost(getVideoCallCost(60) * 0.5);
      }
    }
    
    // Enterprise Package benefits
    if (packageType === 'enterprise') {
      if (selectedDuration === 15) {
        // Unlimited free
        setUsingPackage(true);
        setCost(0);
      } else if (selectedDuration === 30) {
        // Free 5 times/month
        setUsingPackage(true);
        setCost(0);
      } else if (selectedDuration === 60) {
        // Free 2 times/month
        setUsingPackage(true);
        setCost(0);
      }
    }
  };
  
  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime || !selectedDuration) {
      alert('กรุณาเลือกวันที่ เวลา และระยะเวลา');
      return;
    }
    
    // Check T Coins if not using package
    const totalTCoins = getTotalTCoins();
    if (!usingPackage && cost > 0 && totalTCoins < cost) {
      alert(`T Coins ไม่พอ (ต้องการ ${cost} T Coins, มี ${totalTCoins} T Coins)`);
      return;
    }
    
    try {
      setLoading(true);
      
      const bookingData = {
        storeId: store.id,
        ownerId: currentUser.uid,
        purpose,
        purposeDescription: purposeDescription || '',
        date: selectedDate,
        time: selectedTime,
        duration: selectedDuration,
        cost: cost,
        paidBy: usingPackage ? 'package' : 'tcoins',
        packageUsed: usingPackage ? packageInfo?.packageType : null,
        status: 'pending',
      };
      
      const result = await createAdminVideoCallBooking(bookingData);
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      // Deduct T Coins if needed (ใช้ real coins ก่อน ถ้าไม่พอใช้ virtual)
      if (!usingPackage && cost > 0) {
        const totalReal = getTotalReal();
        const totalVirtual = getTotalVirtual();
        
        // ใช้ real coins ก่อน ถ้าไม่พอใช้ virtual
        if (totalReal >= cost) {
          await deductTCoins(currentUser.uid, 'real', cost, `admin_video_call_booking_${selectedDuration}min`);
        } else if (totalReal + totalVirtual >= cost) {
          // ใช้ real ทั้งหมด แล้วใช้ virtual ที่เหลือ
          if (totalReal > 0) {
            await deductTCoins(currentUser.uid, 'real', totalReal, `admin_video_call_booking_${selectedDuration}min_partial`);
          }
          const remainingCost = cost - totalReal;
          await deductTCoins(currentUser.uid, 'virtual', remainingCost, `admin_video_call_booking_${selectedDuration}min_virtual`);
        } else {
          throw new Error(`T Coins ไม่พอ (ต้องการ ${cost} T Coins)`);
        }
      }
      
      alert('จอง Video Call สำเร็จ! แอดมินจะตรวจสอบและแจ้งผลภายใน 24 ชั่วโมง');
      
      if (onSuccess) {
        onSuccess();
      }
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error creating video call booking:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              📹 จอง Video Call กับแอดมิน
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>
          
          {/* Purpose */}
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-2">
              วัตถุประสงค์
            </label>
            <select
              value={purpose}
              onChange={(e) => {
                // Purpose is passed as prop, but we can show it
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              disabled
            >
              <option value="verification">ตรวจสอบร้านค้า</option>
              <option value="on_site_inspection">ตรวจสอบนอกสถานที่</option>
              <option value="inquiry">สอบถามปัญหา</option>
              <option value="other">อื่นๆ</option>
            </select>
          </div>
          
          {/* Purpose Description */}
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-2">
              รายละเอียด (ถ้ามี)
            </label>
            <textarea
              value={purposeDescription}
              onChange={(e) => setPurposeDescription(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              rows="3"
              placeholder="อธิบายวัตถุประสงค์เพิ่มเติม..."
            />
          </div>
          
          {/* Date Selection */}
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-2">
              เลือกวันที่ <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            />
          </div>
          
          {/* Time Selection */}
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-2">
              เลือกเวลา <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-4 gap-2">
              {availableTimes.map((time) => (
                <button
                  key={time}
                  onClick={() => setSelectedTime(time)}
                  className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                    selectedTime === time
                      ? 'bg-green-500 text-white border-green-500'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-green-500'
                  }`}
                >
                  🕐 {time}
                </button>
              ))}
            </div>
          </div>
          
          {/* Duration Selection */}
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-2">
              ระยะเวลา <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {[
                { duration: 15, label: '15 นาที' },
                { duration: 30, label: '30 นาที' },
                { duration: 60, label: '60 นาที' },
              ].map((option) => {
                const optionPrice = getVideoCallCost(option.duration);
                const isSelected = selectedDuration === option.duration;
                const packageDiscount = usingPackage && packageInfo?.packageType === 'premium' && option.duration === 60;
                const displayPrice = packageDiscount ? optionPrice * 0.5 : optionPrice;
                const isFree = usingPackage && cost === 0 && isSelected;
                
                return (
                  <label
                    key={option.duration}
                    className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer ${
                      isSelected
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-300 hover:border-green-300'
                    }`}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="duration"
                        value={option.duration}
                        checked={isSelected}
                        onChange={() => setSelectedDuration(option.duration)}
                        className="mr-3"
                      />
                      <span className="font-semibold">{option.label}</span>
                    </div>
                    <div className="text-right">
                      {isFree && isSelected ? (
                        <span className="text-green-600 font-bold">ฟรี (Package)</span>
                      ) : packageDiscount && isSelected ? (
                        <span>
                          <span className="text-gray-400 line-through">{optionPrice.toLocaleString()}</span>
                          <span className="text-green-600 font-bold ml-2">{displayPrice.toLocaleString()} T Coins</span>
                        </span>
                      ) : (
                        <span className="text-gray-700">{optionPrice.toLocaleString()} T Coins</span>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
            
            {/* Package Benefits Info */}
            {packageInfo && (
              <div className="mt-3 bg-blue-50 p-3 rounded-lg text-sm text-gray-700">
                {packageInfo.packageType === 'premium' && (
                  <p>
                    📦 Premium Package: 15 นาที ฟรี 2 ครั้ง/เดือน, 30 นาที ฟรี 1 ครั้ง/เดือน, 60 นาที ลด 50%
                  </p>
                )}
                {packageInfo.packageType === 'enterprise' && (
                  <p>
                    📦 Enterprise Package: 15 นาที ฟรีไม่จำกัด, 30 นาที ฟรี 5 ครั้ง/เดือน, 60 นาที ฟรี 2 ครั้ง/เดือน
                  </p>
                )}
              </div>
            )}
          </div>
          
          {/* Cost Summary */}
          <div className="mb-6 bg-yellow-50 p-4 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-gray-700">ค่าใช้จ่าย:</span>
              <span className="text-2xl font-bold text-green-600">
                {cost === 0 ? 'ฟรี (ใช้ Package)' : `${cost.toLocaleString()} T Coins`}
              </span>
            </div>
            <div className="flex justify-between items-center text-sm text-gray-600">
              <span>💳 T Coins ที่เหลือ:</span>
              <span>{getTotalTCoins().toLocaleString()} T Coins</span>
            </div>
            {usingPackage && (
              <div className="mt-2 text-sm text-green-600">
                ✅ ใช้สิทธิ์จาก Package: {packageInfo?.packageType?.toUpperCase()}
              </div>
            )}
          </div>
          
          {/* Submit Button */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              disabled={loading}
            >
              ยกเลิก
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !selectedDate || !selectedTime || !selectedDuration}
              className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? 'กำลังจอง...' : 'จอง Video Call'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminVideoCallBookingModal;

